import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { ErrorState, Skeleton } from '../ui/states';
import { ChartHelp, type ChartHelpKey } from './chart-help';
import { displayDate } from '../../lib/date';
import { isForbiddenMessage } from '../../lib/error-message';

export interface ChartFrameProps {
  readonly title: string;
  readonly subtitle?: string;
  /**
   * ⚠️ Thời điểm đang xem. Không có prop này thì Replay (S9) phải viết lại toàn
   * bộ tầng chart — docs/frontend-guide.md §4 gọi đây là lỗi tốn kém nhất có
   * thể mắc trong dự án này.
   *
   * `undefined` = hiện tại. Có giá trị ⇒ khung tự gắn nhãn "đang xem quá khứ",
   * và **mọi** chart được nhãn đó miễn phí. Bắt từng chart tự nhớ hiện nhãn là
   * bắt chúng cùng quên.
   */
  readonly asOf?: Date | undefined;
  /**
   * Khoá i18n của phần "cách đọc biểu đồ này" — APM-CHART-a.
   *
   * Đặt ở khung chứ không để từng chart tự dựng nút, cùng lý do với `asOf`: một
   * thứ mà **mọi** biểu đồ phải có thì phải nằm ở chỗ mọi biểu đồ đi qua, nếu
   * không nó sẽ có ở mười cái đầu rồi thiếu ở mười cái sau.
   *
   * Nội dung khai ở `chartHelp.<key>` — xem `ChartHelp`.
   */
  readonly help?: ChartHelpKey;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly onRetry?: () => void;
  /** Không có dữ liệu: nói **vì sao trống** và bước tiếp theo, đừng vẽ khung rỗng. */
  readonly empty?: { title: string; description?: string } | null;
  /** Chú thích màu. Màu lấy từ token RAG, không hardcode. */
  readonly legend?: { label: string; color: string }[];
  /** Bảng drill-down mở ra dưới biểu đồ khi người dùng click vào một phần. */
  readonly drillDown?: ReactNode;
  readonly actions?: ReactNode;
  /**
   * Icon chú thích cạnh tiêu đề — vd `AuditNotice` của bảng hiệu suất. Chỗ
   * này thay cho dải cảnh báo cả dòng trong thân bảng (2026-09-30).
   */
  readonly notice?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Khung chuẩn cho mọi chart — APM-801, docs/frontend-guide.md §4.
 *
 * Nó tồn tại để ba tính chất bắt buộc không thể quên được:
 *
 * 1. **`asOf`** nằm ở chữ ký, nên một chart không nhận nó sẽ không biên dịch
 *    được vào khung này.
 * 2. **Drill-down** có sẵn một chỗ để đổ vào — chart chỉ cần bắn sự kiện click,
 *    không phải tự dựng panel.
 * 3. **Bốn trạng thái màn hình** (đang tải / lỗi / trống / có dữ liệu) xử lý
 *    một lần ở đây thay vì mỗi chart tự nghĩ lại, và nghĩ thiếu.
 */
export function ChartFrame({
  title,
  subtitle,
  asOf,
  help,
  loading = false,
  error = null,
  onRetry,
  empty = null,
  legend,
  drillDown,
  actions,
  notice,
  children,
}: ChartFrameProps): ReactNode {
  const { t } = useTranslation();

  /*
    403 chung ⇒ ẩn **cả khung**, cả tiêu đề — người dùng yêu cầu 2026-09-30:
    người không được xem khối này thì coi như nó không có ở đó. Chỉ ẩn phần
    thân sẽ để lại một tiêu đề trơ trọi, tức vẫn là một lời thông báo.
  */
  if (isForbiddenMessage(error, t)) return null;

  return (
    // `chart-frame`: móc để một trang mặc áo riêng cho mọi khung của nó (Tổng
    // quan dùng kính — `.overview-glass .chart-frame` ở index.css).
    <section className="chart-frame flex flex-col gap-2 rounded-DEFAULT border border-border p-3">
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium">{title}</h3>
        {help && <ChartHelp helpKey={help} />}
        {notice}
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}

        {asOf && (
          // Một biểu đồ đang vẽ quá khứ mà không nói ra là một biểu đồ nói dối
          // về hiện tại.
          <span className="inline-flex items-center gap-1 rounded-full border border-rag-amber/40 bg-rag-amber/10 px-2 py-0.5 text-xs text-rag-amber">
            <Clock aria-hidden className="size-3.5" />
            {t('chart.asOf', { at: displayDate(asOf) })}
          </span>
        )}

        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </header>

      {legend && legend.length > 0 && (
        <ul className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {legend.map((entry) => (
            <li key={entry.label} className="inline-flex items-center gap-1.5">
              {/* Màu không phải kênh thông tin duy nhất: luôn kèm nhãn chữ. */}
              <span
                aria-hidden
                className="size-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.label}
            </li>
          ))}
        </ul>
      )}

      {loading ? (
        <Skeleton className="h-56 w-full" />
      ) : error ? (
        <ErrorState message={error} {...(onRetry ? { onRetry } : {})} />
      ) : empty ? (
        <div className="flex h-56 flex-col items-center justify-center gap-1 rounded-DEFAULT border border-dashed border-border text-center">
          <p className="text-sm font-medium">{empty.title}</p>
          {empty.description && (
            <p className="max-w-sm text-xs text-muted-foreground">{empty.description}</p>
          )}
        </div>
      ) : (
        children
      )}

      {drillDown}
    </section>
  );
}
